import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import {
  AnimateIn,
  Container,
  ExternalLink,
  Heading,
  InternalLink,
  media,
  TitleSet,
  Text, colors
} from '../ui-components';
import Statistic from '../ui-components/components/statistic';

import renderRichText from '../utils/render-rich-text';
import { isInternalLink } from '../utils/link-handler';
import renderPointOfViewSquares from "./point-of-view-squares";

import Video from './video';

const propTypes = {
  pointOfView: PropTypes.object.isRequired,
  geoSpace: PropTypes.string,
  featuredContent: PropTypes.bool,
  secondary: PropTypes.bool,
  showNumbers: PropTypes.bool,
  layout: PropTypes.string
};

/**
 * Renders the Point Of View Section (image and video), along with optional POV items
 *
 * Currently this section is typically used outside <Paper> tags (i.e. on a white background).
 * When placing on a white background above paper breaks, the default behavior is to have no section bottom-padding.
 * If component is on a white background and above featured content (card or video component generally), use featured
 * content flag to adjust section spacing. Other padding cases will need manually edited with styled components.
 *
 * Images, videos (currently not used but supported), titleIcons, and statistics will render only if supplied via content.
 * Statistics and list items will only render in image/video primary layout.
 *
 * Variant order of precedence: if secondary flag is used, it will override other variants. If titleIcon's are
 * provided from content, showNumbers will be overridden.
 *
 * @component PointOfViewSection
 * 
 * @param {object} props
 * 
 * @prop {object} pointOfView
 * @prop {string} geoSpace
 * @prop {boolean} featuredContent - include flag if featured content is below, adjusts section spacing
 * @prop {string} layout - primary, secondary, or showNumbers. secondary and showNumbers override respective props.
 * @prop {boolean} secondary - include for secondary size variant, adjusts text size
 * @prop {boolean} showNumbers - include to show numbers behind POV items
 * 
 * @returns {JSX}
 */
const PointOfViewSection = props => {
  const { pointOfView, geoSpace, featuredContent, layout, loadImageEager = false } = props;
  const imageLeft = pointOfView?.imageLeft;

  let secondary = layout === 'secondary' ? true : props?.secondary;
  let showNumbers = layout === 'showNumbers' ? true : props?.showNumbers;
  showNumbers = secondary ? null : showNumbers;

  let image = pointOfView?.image?.fluid;
  let video = pointOfView?.video;

  const renderTextSection = () => {
    return (
      <TextContainer id="pov">
        <TitleSet
          label={pointOfView?.label || 'Point Of View'}
          title={pointOfView?.title}
          bodyText={
            pointOfView?.bodyRichJSON ? renderRichText(pointOfView?.bodyRichJSON, geoSpace) : pointOfView?.bodyString
          }
          alignLeft={image || video}
        />

        {pointOfView?.ctaUrl &&
          (isInternalLink(pointOfView?.ctaUrl) ? (
            <InternalLink withArrow hoverUnderline to={pointOfView.ctaUrl}>
              {pointOfView.ctaText || 'Explore Further'}
            </InternalLink>
          ) : (
            <ExternalLink withArrow hoverUnderline href={pointOfView.ctaUrl}>
              {pointOfView.ctaText || 'Explore Further'}
            </ExternalLink>
          ))}
      </TextContainer>
    );
  };

  /**
   * Renders the Point Of View Columns
   *
   */
  const renderPointOfViewColumns = () => {
    const itemsCount = pointOfView?.items?.length;

    const renderPOVColumns = () => {
      return pointOfView?.items?.map((item, index) => {
        const titleIcon = getImage(item?.titleIcon);

        return (
          <ColumnWrapper key={item.id}>
            {showNumbers && <Number>{index + 1}</Number>}
            <Column showNumbers={showNumbers} image={image || video} titleIcon={titleIcon}>
              <HeadingWrapper level={secondary ? 5 : 4} showNumbers={showNumbers} image={image || video}>
                {titleIcon && <TitleIcon image={titleIcon} alt={item.titleIcon?.title ?? `${item.title} icon`} />}
                {item.title}
              </HeadingWrapper>
              <ContentWrapper
                showNumbers={showNumbers}
                image={image || video}
                showBullets={(image || video) && !secondary && !showNumbers && !titleIcon}
              >
                {item.content?.raw
                  ? renderRichText(item.content, geoSpace, secondary ? 'medium' : 'large')
                  : item.content && <Text size={secondary ? 'medium' : 'large'}>{item.content}</Text>}
                {item.stat && item.statContent && (image || video) && !secondary && !showNumbers && !titleIcon && (
                  <StatWrapper>
                    <Statistic stat={item.stat} statContent={item.statContent} />
                  </StatWrapper>
                )}
              </ContentWrapper>
            </Column>
          </ColumnWrapper>
        );
      });
    };

    return (
      <Columns itemsCount={itemsCount} showNumbers={showNumbers}>
        {renderPOVColumns()}
      </Columns>
    );
  };

  /**
   * Renders the Point Of View Image Section
   *
   */
  const renderPointOfViewImage = () => {
    const renderImageSection = () => {
      return (
        image && (
          <StyledGatsbyImage
            loading={loadImageEager ? 'eager' : 'lazy'}
            fetchpriority={loadImageEager ? 'high' : null}
            image={image}
            alt={pointOfView?.image?.alt || pointOfView?.image?.title}
          />
        )
      );
    };

    return (
      <AnimateIn onScroll>
        <POVImageContainer id="pointOfView" sectionContainer featuredContent={featuredContent} image={image} showNumbers={showNumbers} layout={layout}>
          <ImageContainer imageLeft={imageLeft} image={image}>
            {imageLeft ? renderImageSection() : renderTextSection(geoSpace)}
            {imageLeft ? renderTextSection(geoSpace) : renderImageSection()}
          </ImageContainer>
          {layout === 'blocks' && pointOfView?.items ? (renderPointOfViewSquares(pointOfView.items, geoSpace)) : renderPointOfViewColumns()}
        </POVImageContainer>
      </AnimateIn>
    );
  };

  /**
   * Renders the Point Of View Video Section
   */
  const renderPointOfViewVideo = () => {
    const renderVideoSection = () => {
      return (
        <VideoContainer>
          <POVVideo embedUrl={pointOfView.video.embedUrl} />
          <Text italic>{pointOfView.video?.videoDescription}</Text>
        </VideoContainer>
      );
    };

    return (
      <AnimateIn onScroll>
        <POVVideoContainer id="pointOfView" sectionContainer>
          {imageLeft ? renderVideoSection() : renderTextSection()}
          {imageLeft ? renderTextSection() : renderVideoSection()}
        </POVVideoContainer>
        <POVImageContainer id="pointOfView">
        {layout === 'blocks' && pointOfView?.items ? (renderPointOfViewSquares(pointOfView.items, geoSpace)) : renderPointOfViewColumns()}
        </POVImageContainer>
      </AnimateIn>
    );
  };

  if (pointOfView?.video) {
    return renderPointOfViewVideo();
  } else {
    return renderPointOfViewImage();
  }
};

// If showNumbers, gap is 80px - 16px padding.
const POVImageContainer = styled(Container)`
  padding-bottom: ${({ featuredContent }) => (featuredContent ? '80px' : '0')};
  gap: ${({ image, featuredContent, showNumbers, layout }) => (
    layout === 'blocks' ? '120px' :
      showNumbers ? '64px' :
        (!image && featuredContent) ? '80px' : 
          '40px'
  )};

  @media screen and (max-width: ${media.mediumTop}) {
    gap: ${({ layout }) => (layout === 'blocks' ? '60px' : '40px')};
    padding-bottom: ${({ featuredContent }) => (featuredContent ? '60px' : '0')};
    text-align: center;
  }
`;

const ImageContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 100px;
  align-items: ${({ image }) => (image ? '' : 'center')};
  text-align: ${({ image }) => (image ? '' : 'center')};
  
  @media screen and (max-width: ${media.largeTop}) {
    gap: 80px;
  }

  @media screen and (max-width: ${media.mediumTop}) {
    flex-direction: ${({ imageLeft }) => (imageLeft ? 'column-reverse' : 'column')};
    width: 100%;
    height: 100%;
    gap: 40px;

    img {
      width: 100%;
    }

    div {
      width: 100%;
    }
  }
`;

const POVVideoContainer = styled(Container)`
  flex-direction: row;
  gap: 100px;
  justify-content: space-between;

  @media screen and (max-width: ${media.largeTop}) {
    gap: 80px;
  }
  
  @media screen and (max-width: ${media.mediumTop}) {
    align-items: center;
    gap: 20px;
    flex-direction: column;
    text-align: center;
  }
`;

const Columns = styled.div`
  display: ${({ showNumbers }) => (showNumbers ? 'flex' : 'grid')};
  flex-direction: row;
  gap: 40px;
  grid-template-columns: ${({ itemsCount }) => (
    itemsCount === 1 ? '1fr' : 
      itemsCount === 3 ? '1fr 1fr 1fr' :
        '1fr 1fr'
  )};
  
  @media screen and (max-width: ${media.mediumTop}) {
    flex-direction: column;
    grid-template-columns: 1fr;
  }
`;

const ColumnWrapper = styled.div`
  flex: 1 0 0;
`;

const Number = styled.div`
  color: ${colors.secondaryBackground};
  font-family: Tiempos Headline;
  font-size: 120px;
  font-weight: 500;
  line-height: 64px;
  position: absolute;
  z-index: 0;
  
  @media screen and (max-width: ${media.mediumTop}) {
    font-size: 100px;
    position: relative;
  }
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: flex-start;
  gap: ${({ titleIcon }) => (titleIcon ? '0' : '12px')};
  padding: ${({ showNumbers }) => (showNumbers ? '16px 0 0 24px' : '')};
  
  p {
    text-align: left;
  }
  
  @media screen and (max-width: ${media.mediumTop}) {
    align-items: ${({ image }) => (image ? '' : 'center')};
    gap: 12px;
    margin-top: ${({ showNumbers }) => (showNumbers ? '-48px' : '')};
    padding: ${({ showNumbers }) => (showNumbers ? '0' : '')};

    p {
      text-align: ${({ image }) => (image ? 'left' : 'center')};
    }
  }
  
  @media screen and (max-width: ${media.mediumBottom}) {
    margin-top: ${({ showNumbers }) => (showNumbers ? '-39px' : '')};
  }
`;

const TitleIcon = styled(GatsbyImage)`
  height: 80px;
  width: 80px;
`;

const HeadingWrapper = styled(props => <Heading {...props} />)`
  display: flex;
  flex-direction: row;
  gap: 12px;
  align-items: center;
  z-index: ${({ showNumbers }) => (showNumbers ? '1' : '')};

  @media screen and (max-width: ${media.mediumTop}) {
    flex-direction: column;
    gap: 0;   
    text-align: ${({ image }) => (image ? 'left' : 'center')};
  }
`;

const ContentWrapper = styled.div`
  // Getting rid of renderRichText margins
  p, li#styled-rich-list-item {
    margin: 0;
    text-align: left;
    
    &p {
      margin: 0;
    }
    
    @media screen and (max-width: ${media.mediumTop}) {
      text-align: ${({ image }) => (image ? 'left' : 'center')};
    }
  }
  
  li#styled-rich-list-item {
    display: ${({ showBullets }) => (showBullets ? '' : 'none')};
  }

  display: flex;
  flex-direction: column;
  gap: 12px;
  z-index: ${({ showNumbers }) => (showNumbers ? '1' : '')};
  
  ul {
    margin-top: 8px;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
`;

const StatWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
  width: 100%;

  @media screen and (max-width: ${media.mediumTop}) {
    text-align: left;
  }
`;

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  max-width: 680px;
  text-align: left;
`;

const StyledGatsbyImage = styled(GatsbyImage)`
  height: 380px;
  max-width: 540px;
  
  @media screen and (max-width: ${media.mediumTop}) {
    max-width: 100%;
  }
  
  @media screen and (max-width: ${media.smallTop}) {
    height: 280px;
  }
  
  @media screen and (max-width: ${media.smallBottom}) {
    height: 230px;
  }
`;

const VideoContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 500px;

  @media screen and (max-width: ${media.mediumTop}) {
    justify-content: center;
    align-items: center;
    max-width: 100%;
  }
`;

const POVVideo = styled(Video)`
  width: 500px;
  height: 500px;
  margin: 0;

  @media screen and (max-width: ${media.mediumTop}) {
    max-width: 300px;
    width: 100%;
    height: 300px;
  }
`;

PointOfViewSection.propTypes = propTypes;

export default PointOfViewSection;
